import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    useCallback,
} from "react";
import RegistrateStep from "./RegistrateStep";
import AuthStep from "./AuthStep";
import RegistrationStep from "./RegistrationStep";
import "./telephone_auth.css";
import {
    login,
    verifyPhoneNumber,
    checkPhone,
    registerFull,
} from "../../../../../http/userAPI";
import {Context} from "../../../../../index";
import {observer} from "mobx-react-lite";
import {runInAction} from 'mobx';
import {useNavigate} from "react-router-dom";
import WelcomStep from "./WelcomStep";
import BasketStep from "./basketStapeComponent/BasketStep";
import RegistrationOrNot from "./registrationOrNot/RegistrationOrNot";
import {syncFavorites} from "../../../../../http/favoriteApi";
import {ReactComponent as CloseIcon} from "../../../../../assets/icons/close.svg";
import {ReactComponent as ArrowIcon} from "../../../../../assets/icons/arrow_bottom.svg";
import {useCaptcha} from "../../../../../hooks/useCaptcha";
import {SmartCaptcha} from "@yandex/smart-captcha";
import {useDeviceUUID} from "../../../../../hooks/useDeviceUUID";

const ModalAuth = observer((props) => {
    const [userAgent, setUserAgent] = useState("");
    const [codeLimitErrorText, setCodeLimitErrorText] = useState("");
    const originalViewportRef = useRef(null);
    const mountedRef = useRef(true);
    const flowRef = useRef(0);
    const attemptRef = useRef(null);

    const {getDeviceUUID} = useDeviceUUID();

    const {main, settings, basket, user} = useContext(Context);
    const session = main.basketSession;
    let navigate = useNavigate();

    const {
        captchaVisible,
        captchaKey,
        showCaptcha,
        onCaptchaSuccess,
        onTokenExpired,
        onNetworkError,
        onJavascriptError,
        resetCaptcha,
    } = useCaptcha();

    const restoreViewport = useCallback(() => {
        const captured = originalViewportRef.current;
        if (captured === null) return;
        originalViewportRef.current = null;
        if (captured.content === null) captured.element.removeAttribute('content');
        else captured.element.setAttribute('content', captured.content);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        setUserAgent(window.navigator.userAgent);
        return () => {
            mountedRef.current = false;
            flowRef.current += 1;
            session.cancelAttempt(attemptRef.current);
            resetCaptcha();
            restoreViewport();
        };
    }, [session, resetCaptcha, restoreViewport]);

    const cancelFlow = () => {
        flowRef.current += 1;
        session.cancelAttempt(attemptRef.current);
        attemptRef.current = null;
        restoreViewport();
    };
    const flowCurrent = flow => mountedRef.current && flowRef.current === flow;

    const handleChallengeVisible = useCallback(() => {
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (!viewportMeta) return;

        if (originalViewportRef.current === null) {
            originalViewportRef.current = {
                element: viewportMeta,
                content: viewportMeta.getAttribute('content'),
            };
        }

        viewportMeta.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        );
    }, []);

    const handleChallengeHidden = useCallback(() => {
        resetCaptcha();
        restoreViewport();
    }, [resetCaptcha, restoreViewport]);

    const handleSuccess = useCallback(
        (token) => {
            onCaptchaSuccess(token);
            restoreViewport();
        },
        [onCaptchaSuccess, restoreViewport]
    );

    const captchaNode = captchaVisible && <div className="captcha">
        <SmartCaptcha
            key={captchaKey}
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
            onSuccess={handleSuccess}
            onTokenExpired={onTokenExpired}
            onNetworkError={onNetworkError}
            onJavascriptError={onJavascriptError}
            onChallengeHidden={handleChallengeHidden}
            onChallengeVisible={handleChallengeVisible}
            visible={captchaVisible}
            hideShield={true}
        />
    </div>;

    let registrationOnFlyBasket = basket.registrationOnFlyBasket;

    const changeSmsCodeValue = (string) => {
        let replaceCode = string.replace(/[^\d]/g, "");
        user.setAuthSmsCode(replaceCode);
    };

    const goToPhoneStep = () => {
        cancelFlow();
        user.setPhoneNumber("");
        user.setStepModals("registration");
        user.setCounterSecondCode(0);
        setCodeLimitErrorText("");
        user.setJustRegistered(false);
        user.resetRegistrationFormData();
    };

    const goToRegistrationForm = () => {
        cancelFlow();
        if (user.phoneNumber) {
            user.setRegistrationFormData({
                phone: user.phoneNumber,
                country: user.country || "Россия",
            });
            user.setStepModals("registrationForm");
        } else {
            user.setStepModals("registrationForm");
        }
        setCodeLimitErrorText("");
    };

    const sendAuthCode = async (phoneNumber, country, token) => {
        const flow = flowRef.current;
        const deviceUUID = getDeviceUUID();

        try {
            const response = await verifyPhoneNumber(
                phoneNumber,
                country,
                token,
                deviceUUID,
                userAgent
            );
            if (!flowCurrent(flow)) return false;
            if (response.data.success) {
                user.setStepModals("authStep");
                user.setCounterSecondCode(60);
                setCodeLimitErrorText("");
                return true;
            } else {
                setCodeLimitErrorText(
                    response.data.error || "Ошибка отправки кода"
                );
                return false;
            }
        } catch (error) {
            if (!flowCurrent(flow)) return false;
            setCodeLimitErrorText(
                error.response?.data?.error || "Ошибка отправки кода"
            );
            return false;
        }
    };

    const checkPhoneNumber = async (phone, country) => {
        const flow = flowRef.current;
        let replacePhoneNumber = phone.replace(/[^\d]/g, "");
        if (replacePhoneNumber.length < 10) {
            setCodeLimitErrorText("Введите корректный номер телефона");
            return false;
        }

        const deviceUUID = getDeviceUUID();

        try {
            const token = await showCaptcha();
            if (!flowCurrent(flow)) return false;

            const response = await checkPhone({
                telephone: replacePhoneNumber,
                token: token,
                deviceUUID,
                userAgent,
                country: country || "Россия",
            });
            if (!flowCurrent(flow)) return false;

            if (response.exists) {
                user.setJustRegistered(false);
                user.setPhoneNumber(phone);
                user.setCountry(country || "Россия");
                const newToken = await showCaptcha();
                if (!flowCurrent(flow)) return false;
                await sendAuthCode(
                    replacePhoneNumber,
                    country || "Россия",
                    newToken
                );
                return true;
            } else {
                user.setRegistrationFormData({
                    phone: phone,
                    country: country || "Россия",
                });
                user.setStepModals("registrationForm");
                setCodeLimitErrorText("");
                return true;
            }
        } catch (error) {
            if (!flowCurrent(flow)) return false;
            setCodeLimitErrorText(
                error.response?.data?.error ||
                (error.code === "CAPTCHA_UNAVAILABLE" ? error.message : "Ошибка проверки телефона")
            );
            return false;
        }
    };

    const handleRegistration = async (formData) => {
        const flow = flowRef.current;
        let replacePhoneNumber = formData.telephone.replace(/[^\d]/g, "");
        const deviceUUID = getDeviceUUID();

        try {
            const token = await showCaptcha();
            if (!flowCurrent(flow)) return {success: false};

            const response = await registerFull({
                telephone: replacePhoneNumber,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName || "",
                city: formData.city || "",
                country: formData.country || "Россия",
                consentEmail: formData.consentEmail,
                consentSms: formData.consentSms,
                consentPersonalData: formData.consentPersonalData,
                deviceUUID: deviceUUID,
                token: token,
            });
            if (!flowCurrent(flow)) return {success: false};

            if (response.success) {
                user.setJustRegistered(true);
                user.setPhoneNumber(
                    formData.telephoneMasked || replacePhoneNumber
                );
                user.setCountry(formData.country || "Россия");
                const newToken = await showCaptcha();
                if (!flowCurrent(flow)) return {success: false};
                await sendAuthCode(
                    replacePhoneNumber,
                    formData.country || "Россия",
                    newToken
                );
                return {success: true};
            } else {
                const errorMsg = response.error || "Ошибка регистрации";
                setCodeLimitErrorText(errorMsg);
                return {success: false, error: errorMsg};
            }
        } catch (error) {
            if (!flowCurrent(flow)) return {success: false};
            const errorMsg =
                error.response?.data?.error ||
                (error.code === "CAPTCHA_UNAVAILABLE" ? error.message : "Ошибка регистрации");
            setCodeLimitErrorText(errorMsg);
            return {success: false, error: errorMsg};
        }
    };

    const getCodeOneMoreTime = async () => {
        const flow = flowRef.current;
        if (user.counterSecondCode > 0) {
            return false;
        }

        let country = user.country || "Россия";
        let replacePhoneNumber = user.phoneNumber.replace(/[^\d]/g, "");

        const deviceUUID = getDeviceUUID();

        try {
            const token = await showCaptcha();
            if (!flowCurrent(flow)) return false;
            const response = await verifyPhoneNumber(
                replacePhoneNumber,
                country,
                token,
                deviceUUID,
                userAgent
            );
            if (!flowCurrent(flow)) return false;
            user.setAuthSmsCode("");
            user.setCodeMessage("");
            if (response.data.success) {
                user.setCounterSecondCode(60);
                setCodeLimitErrorText("");
            } else {
                setCodeLimitErrorText(response.data.error);
            }
        } catch (error) {
            if (flowCurrent(flow)) setCodeLimitErrorText(
                error.code === "CAPTCHA_UNAVAILABLE" ? error.message : "Ошибка отправки кода"
            );
        }
    };

    const signOn = async () => {
        if (user.authSmsCode.length < 4) {
            setCodeLimitErrorText("Введите код из 4 цифр");
            return false;
        }
        let replacePhoneNumber = user.phoneNumber.replace(/[^\d]/g, "");
        const deviceUUID = getDeviceUUID();

        cancelFlow();
        setCodeLimitErrorText("");
        const flow = flowRef.current;
        const attempt = session.startLoginAttempt();
        attemptRef.current = attempt;
        const currentAttempt = () => flowCurrent(flow) && session.isCurrent(attempt);
        let tokenBranchSeen = false;
        const canCommitCurrentToken = () => {
            if (!currentAttempt()) return false;
            tokenBranchSeen = true;
            return true;
        };
        let data;
        try {
            data = await login(replacePhoneNumber, user.authSmsCode, deviceUUID,
                {canCommitToken: canCommitCurrentToken, commitContext: attempt});
        } catch {
            if (currentAttempt()) {runInAction(() => session.failAuth(attempt));setCodeLimitErrorText("Не удалось войти. Попробуйте ещё раз.");}
            return false;
        }
        if (!currentAttempt()) return false;
        if (data?.message) {
            runInAction(() => session.failAuth(attempt));
            user.setCodeMessage(data.message);
            setCodeLimitErrorText(data.message);
            return data;
        }
        if (!tokenBranchSeen || !session.acceptAuth(attempt, data)) {
            runInAction(() => session.failAuth(attempt));
            setCodeLimitErrorText("Не удалось войти. Попробуйте ещё раз.");
            return false;
        }
        // Accepted tickets retire. Later effects are owned by the accepted epoch
        // and this modal flow/lifetime, never by an obsolete attempt or only id.
        const epoch = basket.sessionEpoch;
        const currentAccepted = () => flowCurrent(flow) && basket.sessionEpoch === epoch &&
            basket._session?.principalId === data.id && user.isAuth === true && user.user?.id === data.id;

        var favoritesIds = [];

        if (
            typeof settings.favoriteArray != "undefined" &&
            settings.favoriteArray != null &&
            settings.favoriteArray.length > 0
        ) {
            settings.favoriteArray.map((item, index) =>
                favoritesIds.push(item.id)
            );
        }

        syncFavorites(favoritesIds, data.id).then((response) => {
            if (currentAccepted() && response.status == 200 && response.data.res) {
                settings.setFavoriteArray(response.data.favs);
                localStorage.setItem(
                    "favoriteStorage",
                    JSON.stringify(response.data.favs)
                );
            }
        }).catch(() => {});

        user.setCodeMessage("");
        user.setPhoneNumber("");
        if (!registrationOnFlyBasket) user.setStepModals("welcome");
        const result = await session.refresh();
        if (!currentAccepted()) return false;
        if (result.status !== 'accepted') {
            setCodeLimitErrorText("Не удалось обновить корзину. Попробуйте ещё раз.");
            return false;
        }

        if (registrationOnFlyBasket) {
            user.setJustRegistered(false);
            navigate("/personal/basket/");
            user.setShadowOverlay(false);
            user.setOpenAuthModal(0);
            basket.setStartBasketNum(2);
            return false;
        }
    };

    const handleCheckPhone = async () => {
        await checkPhoneNumber(user.phoneNumber, user.country);
    };

    const handleClose = () => {
        cancelFlow();
        user.resetModalState();
        resetCaptcha();
        props.onClose();
    };

    return (
        <div className={"app_telephone_auth " + props.className}>
            <div className="wrapper_modal-overlay">
                <div className="wrapper_modal">
                    {(user.stepModals === "authStep" ||
                        user.stepModals === "registrationForm") && (
                        <button type="button" aria-label="Назад к телефону" style={{border:0,padding:0,background:'none'}}
                            onClick={() => goToPhoneStep()}
                            className="backModal"
                        >
                            <ArrowIcon />
                        </button>
                    )}

                    <button type="button" aria-label="Закрыть авторизацию" style={{border:0,padding:0,background:'none'}} onClick={handleClose} className="closeModal">
                        <CloseIcon />
                    </button>
                    {(user.stepModals === 'authStep' || user.stepModals === 'welcome') &&
                        codeLimitErrorText && codeLimitErrorText !== user.codeMessage && <p role="alert">{codeLimitErrorText}</p>}

                    {user.stepModals === "registration" && (
                        <div className={"wrap_step_components"}>
                            <RegistrateStep
                                controllStateClass="active"
                                registrationFunc={handleCheckPhone}
                                codeLimitErrorText={codeLimitErrorText}
                                goToRegistrationForm={goToRegistrationForm}
                                captchaNode={captchaNode}
                            />
                        </div>
                    )}

                    {user.stepModals === "registrationForm" && (
                        <div className={"wrap_step_components"}>
                            <RegistrationStep
                                controllStateClass="active"
                                codeLimitErrorText={codeLimitErrorText}
                                goToPhoneStep={goToPhoneStep}
                                onRegister={handleRegistration}
                                captchaNode={captchaNode}
                            />
                        </div>
                    )}

                    {user.stepModals === "authStep" && (
                        <div className={"wrap_step_components"}>
                            <AuthStep
                                controllStateClass="active"
                                codeValue={user.authSmsCode}
                                codeChange={(e) =>
                                    changeSmsCodeValue(e.target.value)
                                }
                                goBack={() => goToPhoneStep()}
                                getCodeOneMoreTime={() => getCodeOneMoreTime()}
                                getCodeButtonClass={
                                    user.counterSecondCode > 0
                                        ? "unactive"
                                        : "active"
                                }
                                loginFunc={() => signOn()}
                                codeLimitErrorText={codeLimitErrorText}
                                captchaNode={captchaNode}
                            />
                        </div>
                    )}

                    {user.stepModals === "welcome" && (
                        <div className={"wrap_step_components"}>
                            <WelcomStep
                                controllStateClass="active"
                                closeModal={() => {
                                    cancelFlow();
                                    resetCaptcha();
                                    props.closeClick();
                                    user.resetModalState();
                                }}
                            />
                        </div>
                    )}

                    {(user.stepModals === "basketStep" ||
                        user.stepModals === "orderRegistrationOrNot") && (
                        <div className={"full_modal_wrapper"}>
                            {user.stepModals === "basketStep" && (
                                <BasketStep
                                    controllStateClass="active"
                                    deleteAll={handleClose}
                                />
                            )}
                            {user.stepModals === "orderRegistrationOrNot" && (
                                <RegistrationOrNot />
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
});

export default ModalAuth;
